/**
 * 1098-T record normalization (TS-7958).
 *
 * The legacy Postgres table (legacy.form1098T_<year>) stores the student under
 * STUDENT_* columns (STUDENT_SSN, STUDENT_FIRST/MIDDLE/LAST_NAME, STUDENT_ADDRESS…)
 * and has NO RECIPIENT_* columns at all — but the generators read RECIPIENT_*.
 * On the legacy path that meant undefined TIN/name/address (and a crash on
 * RECIPIENT_ID_NUMBER.replace). The portal path supplies RECIPIENT_* directly.
 *
 * Normalize once at generator entry: prefer the RECIPIENT_* value when present
 * (portal path unchanged), fall back to the STUDENT_* column (legacy path fixed).
 * STUDENT_MIDDLE_NAME / STUDENT_SUFFIX additionally enrich PersonName (the IRIS
 * RecipientDetail PersonName has supported PersonMiddleNm/SuffixNm since TY2025;
 * the columns are new in the TY2026 layout).
 */
export function normalizeStudentRecord<T extends Record<string, any>>(r: T): T {
  return {
    ...r,
    RECIPIENT_ID_NUMBER: r.RECIPIENT_ID_NUMBER ?? r.STUDENT_SSN ?? "",
    RECIPIENT_NAME: r.RECIPIENT_NAME ?? r.STUDENT_FIRST_NAME ?? "",
    RECIPIENT_NAME2: r.RECIPIENT_NAME2 ?? r.STUDENT_LAST_NAME ?? "",
    RECIPIENT_ADDRESS1: r.RECIPIENT_ADDRESS1 ?? r.STUDENT_ADDRESS1,
    RECIPIENT_ADDRESS2: r.RECIPIENT_ADDRESS2 ?? r.STUDENT_ADDRESS2,
    RECIPIENT_CITY: r.RECIPIENT_CITY ?? r.STUDENT_CITY,
    RECIPIENT_STATE: r.RECIPIENT_STATE ?? r.STUDENT_STATE,
    RECIPIENT_ZIP: r.RECIPIENT_ZIP ?? r.STUDENT_ZIP,
  };
}

/** PersonName group with the TY2026-layout middle name / suffix when present. */
export function buildStudentPersonName(
  record: Record<string, any>,
  firstName: string,
  lastName: string
) {
  return {
    PersonFirstNm: firstName,
    ...(record.STUDENT_MIDDLE_NAME && {
      PersonMiddleNm: record.STUDENT_MIDDLE_NAME,
    }),
    PersonLastNm: lastName,
    ...(record.STUDENT_SUFFIX && { SuffixNm: record.STUDENT_SUFFIX }),
  };
}
